// TUT-231 — "quando você clica, por exemplo, nos vencidos... me abre a lista"
// (cliente, 32:17); perguntada se era só na Visão geral ou em tudo: "E tudo,
// né?" (32:35). Todo número que conta uma fila abre a MESMA fila já filtrada.
//
// Este módulo é o de-para ÚNICO entre o que o indicador conta e o que a lista
// filtra. Se cada tela montasse o seu link, um contador acabaria apontando
// para um filtro que conta outra coisa — e o número da origem discordaria do
// total do destino, que é pior do que número nenhum.
//
// Os filtros de URL já existiam (TUT-204): `/pericias?status=` aceita lista,
// `/prazos?status=` aceita um só. Nada novo no servidor.
//
// ---------------------------------------------------------------------------
// TUT-244 — O CONTRATO DESTE MÓDULO FOI RE-DECIDIDO, e o registro é este.
//
// A frase da cliente que originou a TUT-231 — "quando você clica, por exemplo,
// nos vencidos... me abre a lista" — foi lida como NAVEGAÇÃO: o contador vira
// `<Link>` para a lista filtrada. O quadro que ela mandou em 10/09 responde
// outra coisa, e responde por escrito: os quatro cartões cinzentos que ela
// comentou dizem, cada um, "POP UP". A lista abre POR CIMA da tela.
//
// **Escrito ganha de falado** (a mesma regra que corrigiu as colunas da
// Elaboração), então o gesto padrão de um contador passa a ser abrir o foco —
// `lib/foco.ts`, o catálogo que declara a lista, as colunas e o salto de cada
// linha.
//
// **Este módulo não é dívida, e continua vivo por dois motivos.** O primeiro é
// que o NÍVEL 3 da cadeia é navegação de verdade: a seta que sai do rodapé da
// ficha vai para a tela do processo, e é ela que impede a pilha de crescer sem
// fim. O segundo é que as telas que ainda não foram migradas (Visão geral,
// Prazos, Perícias) continuam navegando, e navegar está certo até a fatia
// delas chegar — o que seria errado é as duas leituras conviverem sem ninguém
// dizer qual é a vigente. É esta.
//
// O que saiu junto: `listaDePecasPendentes`, que existia só para os dois
// cartões do Agendamento. Eles abrem pop-up desde a TUT-244, e a função ficou
// sem chamador — de-para sem consumidor é o par que diverge em silêncio.
// ---------------------------------------------------------------------------
// TUT-313 (26/09) — RE-DECIDIDO DE NOVO, agora pelo dono, para os cartões que
// estão NA PRÓPRIA lista que recortam: "quando eu clico em um card tipo
// pendente perícia, seria interessante ele filtrar os cards como estava antes,
// não acho que o pop-up é melhor nesse caso", e "tem que tudo ser igual". A
// regra única desses cartões (Agendamento, Elaboração, Perícias, Prazos):
// "Atribuídos a mim" é ESCOPO; as situações são exclusivas dentro dele; o
// clique mexe só na própria chave da busca; clicar no ativo tira o filtro. O
// pop-up continua o gesto dos contadores que moram FORA da lista (a Visão
// geral simples, Clientes, Financeiro). Aqui, `listaDePrazos` passou a ser
// lido como o RECORTE a mesclar na busca (`search`), não como destino inteiro.
// ---------------------------------------------------------------------------

import type { FiltroDeEntrega } from "./recorte-da-entrega";
import { periodoDaBusca } from "./periodo-de-datas";
import type { EtapaDeLaudo } from "./recorte-de-laudos";
import type { ExpertiseStatusKey } from "./types";

/** As quatro faixas do widget "Prazos por urgência" da Visão geral. */
export type FaixaDePrazo =
  | "overdue"
  | "dueWithin24Hours"
  | "dueWithin72Hours"
  | "dueWithinWindow";

const HORA_MS = 60 * 60 * 1_000;

/**
 * Em que faixa de "Prazos por urgência" cai UM prazo — a segunda passada da
 * TUT-310: "Próximos prazos" pinta cada linha com a cor da faixa do primeiro
 * widget, e as duas listas só concordam se a régua for a mesma. É o espelho de
 * `summarizeDeadlineWindow` (apps/api/src/domain/deadline-window.ts): antes de
 * agora é vencido; até 24 h; até 72 h; o resto. O tom que a API manda em
 * `Deadline.tone` NÃO serve: ele junta vencido e "até 24 h" em `critical`,
 * que é outra pergunta (o lembrete), e pintaria de vermelho o que o primeiro
 * widget conta como laranja. Além da janela o prazo continua no resto — a
 * lista mostra os mais próximos, e ela não corta por janela.
 */
export function faixaDoPrazo(dueAt: string, agora: Date): FaixaDePrazo {
  const restante = new Date(dueAt).getTime() - agora.getTime();
  if (restante < 0) return "overdue";
  if (restante <= 24 * HORA_MS) return "dueWithin24Hours";
  if (restante <= 72 * HORA_MS) return "dueWithin72Hours";
  return "dueWithinWindow";
}

/** Os quatro cartões do topo de `/prazos`. */
export type ResumoDePrazos = "pending" | "overdue" | "dueSoon" | "completed";

/** O status que `/prazos` aceitava na URL (a tela saiu na TUT-568; o endereço redireciona). */
export type StatusDePrazo = "pending" | "overdue" | "completed" | "cancelled";

/**
 * TUT-475 — os recortes de vencimento que `/prazos?vence=` aceita. Espelho de
 * `VENCE_DA_JANELA` (apps/api/src/domain/deadline-window.ts), que o DTO fecha:
 * `24h`, `72h` e `restante` são as três barras do widget "Prazos por
 * urgência", e `janela` é a janela inteira, o cartão "Próximos N dias".
 */
export const VENCE_DE_PRAZO = ["24h", "72h", "restante", "janela"] as const;
export type VenceDePrazo = (typeof VENCE_DE_PRAZO)[number];

/** Lista fechada conferida no ponto de leitura (ver AGENTS, `useSearch`). */
export function isVenceDePrazo(valor: unknown): valor is VenceDePrazo {
  return (
    typeof valor === "string" &&
    (VENCE_DE_PRAZO as readonly string[]).includes(valor)
  );
}

/**
 * TUT-475 — a janela que o link de uma faixa leva (`dias`): a MESMA faixa de
 * `prazos.janela_aviso_dias` e do `janelaDias` do painel, que o DTO da API
 * fecha com `@Min(1) @Max(30)`.
 */
export function isDiasDaJanela(valor: unknown): valor is number {
  return typeof valor === "number" && Number.isInteger(valor) && valor >= 1 && valor <= 30;
}

const dias = (quantos: number) => (quantos === 1 ? "1 dia" : `${quantos} dias`);

/**
 * TUT-475 (revisão 2) — a faixa EXISTE nesta janela? As faixas internas se
 * truncam no fim da janela (o corte do servidor), então "24 a 72 h" é vazia
 * por construção numa janela de 1 dia, e o "restante" numa de até 3 dias.
 * O widget não desenha faixa que não pode ter prazo, e o chip diz que ela
 * está fora da janela.
 */
export function faixaCabeNaJanela(vence: VenceDePrazo, janelaDias: number): boolean {
  switch (vence) {
    case "24h":
    case "janela":
      return true;
    case "72h":
      return janelaDias > 1;
    case "restante":
      return janelaDias > 3;
  }
}

/** A mesma pergunta pela chave do widget; "Vencidos" existe sempre. */
export function faixaDoWidgetCabeNaJanela(
  faixa: FaixaDePrazo,
  janelaDias: number,
): boolean {
  return faixa === "overdue" || faixaCabeNaJanela(VENCE_DO_RECORTE[faixa], janelaDias);
}

/**
 * O texto do chip removível de `/prazos` para cada faixa, verdadeiro em toda
 * janela de 1 a 30 dias. O fim de cada faixa é o fim truncado (janela de 2
 * dias: "de 24 a 48 h"); a faixa vazia por construção — que só se alcança por
 * URL digitada, porque o widget não a desenha — diz que está fora da janela,
 * em vez de um intervalo ao contrário ("de 72 h a 1 dia").
 */
export function rotuloDoVence(vence: VenceDePrazo, janelaDias: number): string {
  const fimDaJanelaEmHoras = janelaDias * 24;
  switch (vence) {
    case "24h":
      return "Vence em até 24 h";
    case "72h":
      return faixaCabeNaJanela(vence, janelaDias)
        ? `Vence de 24 a ${Math.min(72, fimDaJanelaEmHoras)} h`
        : `Vence de 24 a 72 h, fora da janela de ${dias(janelaDias)}`;
    case "restante":
      return faixaCabeNaJanela(vence, janelaDias)
        ? `Vence de 72 h a ${dias(janelaDias)}`
        : `Vence depois de 72 h, fora da janela de ${dias(janelaDias)}`;
    case "janela":
      return `Vence ${janelaDias === 1 ? "no próximo dia" : `nos próximos ${dias(janelaDias)}`}`;
  }
}

/**
 * TUT-475 (revisão 2, N1) — a faixa e a janela que a busca de `/prazos` pode
 * mandar à API, saneadas num lugar só (a rota e a tela chamam esta função).
 * A faixa é lista fechada e só vale com pendentes: com "Vencidos" ou
 * "Concluídos" a API recusa com 400, e uma URL salva ou digitada como
 * `?status=overdue&vence=24h` abriria a tela de erro. `dias` só vale com a
 * faixa e na faixa de 1 a 30.
 */
export function faixaDaBuscaDePrazos(busca: {
  status?: unknown;
  vence?: unknown;
  dias?: unknown;
}): { vence?: VenceDePrazo; dias?: number } {
  if (!isVenceDePrazo(busca.vence)) return {};
  const status = busca.status ?? "";
  if (status !== "" && status !== "pending") return {};
  return {
    vence: busca.vence,
    ...(isDiasDaJanela(busca.dias) ? { dias: busca.dias } : {}),
  };
}

/**
 * TUT-568/TUT-578 — a lista do Agendamento recortada pela entrega: a faixa (ou
 * `todas`, o que tem prazo) ou o período do prazo (a barra da Série).
 */
export interface DestinoDaEntrega {
  to: "/agendamento-lista";
  search: { entrega?: FiltroDeEntrega; entregaDe?: string; entregaAte?: string };
}

export function listaDaEntrega(filtro: FiltroDeEntrega): DestinoDaEntrega {
  return { to: "/agendamento-lista", search: { entrega: filtro } };
}

export function listaDaEntregaNoPeriodo(de: string, ate: string): DestinoDaEntrega {
  return { to: "/agendamento-lista", search: { entregaDe: de, entregaAte: ate } };
}

/** TUT-578 (D5 = Z) — as abas Quesitos e Laudos do quadro, recortadas pela mesma entrega. */
export type OrigemDaElaboracao = "quesitos" | "laudos";

export interface DestinoDaEntregaNaElaboracao {
  to: "/elaboracao";
  search: { atividade: OrigemDaElaboracao; entrega?: FiltroDeEntrega; entregaDe?: string; entregaAte?: string };
}

export function elaboracaoNaEntrega(atividade: OrigemDaElaboracao, filtro: FiltroDeEntrega): DestinoDaEntregaNaElaboracao {
  return { to: "/elaboracao", search: { atividade, entrega: filtro } };
}

export function elaboracaoNaEntregaNoPeriodo(
  atividade: OrigemDaElaboracao,
  de: string,
  ate: string,
): DestinoDaEntregaNaElaboracao {
  return { to: "/elaboracao", search: { atividade, entregaDe: de, entregaAte: ate } };
}

/** TUT-578 (D4) — as entregues do período até o prazo: o `valor` da Medição "Entregas no prazo". */
export interface DestinoDasEntregues {
  to: "/agendamento-lista";
  search: { status: "entregues"; periodoDe: string; periodoAte: string; noPrazo: "sim" };
}

export function listaDasEntreguesNoPrazo(de: string, ate: string): DestinoDasEntregues {
  return { to: "/agendamento-lista", search: { status: "entregues", periodoDe: de, periodoAte: ate, noPrazo: "sim" } };
}

export interface DestinoDePrazos {
  to: "/prazos";
  search: {
    status: StatusDePrazo;
    vence?: VenceDePrazo;
    /** TUT-475 — a janela com que o widget contou a faixa. */
    dias?: number;
    /** TUT-475 — o recorte "meu" do widget: a fila só do responsável. */
    responsavel?: string;
    sort?: "vencimento";
    dir?: "asc";
  };
}

/**
 * TUT-475 — o que o widget sabe e a lista precisa para contar igual: QUEM
 * (o widget desenha os prazos atribuídos à sessão) e COM QUE JANELA (o painel
 * pode ter a própria, TUT-203c). Os cartões de `/prazos` não passam nada: lá
 * o responsável e a janela já são os da própria tela.
 */
export interface RecorteDoWidget {
  responsavel?: string | undefined;
  dias?: number | undefined;
}

/** TUT-475 — o `vence` de cada faixa e do cartão "Próximos N dias". */
const VENCE_DO_RECORTE = {
  dueWithin24Hours: "24h",
  dueWithin72Hours: "72h",
  dueWithinWindow: "restante",
  dueSoon: "janela",
} as const satisfies Partial<Record<FaixaDePrazo | ResumoDePrazos, VenceDePrazo>>;

/**
 * A lista de prazos que corresponde a uma faixa ou cartão.
 *
 * "Vencidos" tem filtro próprio. TUT-475 — as faixas "até 24 h", "24 a 72 h"
 * e "restante" e o cartão "próximos N dias" também têm: `vence`, que o
 * servidor corta com a MESMA régua com que o widget conta
 * (`limitesDaJanela`), então o número clicado é o total do destino. Até a
 * TUT-475 as quatro abriam a mesma lista (todos os pendentes), e o "3" da
 * barra virava o "40" do outro lado. A ordem por vencimento fica: dentro da
 * faixa, o mais urgente primeiro.
 */
export function listaDePrazos(
  recorte: FaixaDePrazo | ResumoDePrazos,
  doWidget: RecorteDoWidget = {},
): DestinoDePrazos {
  const destino = destinoSemOWidget(recorte);
  const quem = doWidget.responsavel ? { responsavel: doWidget.responsavel } : {};
  // A janela só muda o corte de quem tem faixa; em "Vencidos" ela não conta.
  const janela =
    destino.search.vence && doWidget.dias !== undefined ? { dias: doWidget.dias } : {};
  return { ...destino, search: { ...destino.search, ...janela, ...quem } };
}

function destinoSemOWidget(recorte: FaixaDePrazo | ResumoDePrazos): DestinoDePrazos {
  switch (recorte) {
    case "overdue":
      return { to: "/prazos", search: { status: "overdue" } };
    case "completed":
      return { to: "/prazos", search: { status: "completed" } };
    case "pending":
      return { to: "/prazos", search: { status: "pending" } };
    case "dueSoon":
    case "dueWithin24Hours":
    case "dueWithin72Hours":
    case "dueWithinWindow":
      return {
        to: "/prazos",
        search: {
          status: "pending",
          vence: VENCE_DO_RECORTE[recorte],
          sort: "vencimento",
          dir: "asc",
        },
      };
  }
}

/** O pedaço da busca de `/prazos` que os quatro cartões do topo leem e mexem. */
interface RecorteDaBuscaDePrazos {
  status?: string;
  vence?: string;
  dias?: number;
  sort?: string;
  dir?: string;
  page?: number;
}

/**
 * TUT-313 — o cartão de `/prazos` está aceso? TUT-475 — "Pendentes" e
 * "Próximos dias" filtram o MESMO status; quem os separa é a faixa: `janela`
 * acende "Próximos dias", e qualquer faixa acesa (vinda do widget) apaga
 * "Pendentes" — ela aparece como chip, não como cartão.
 */
export function cartaoDePrazosAtivo(
  filtros: RecorteDaBuscaDePrazos,
  recorte: ResumoDePrazos,
): boolean {
  const destino = listaDePrazos(recorte).search;
  if (filtros.status !== destino.status) return false;
  return (filtros.vence ?? undefined) === destino.vence;
}

/**
 * A busca depois do clique num cartão de `/prazos`: o clique mexe só no
 * recorte, e clicar no ativo tira o filtro. TUT-472 — "Pendentes" também LIMPA
 * a ordenação por vencimento que "Próximos dias" pôs. Só a por VENCIMENTO: quem
 * ordenou por outra coluna (processo, responsável) a mantém. TUT-475 — todo
 * clique solta a faixa (`vence`) que não é a do cartão: "Vencidos" com
 * `vence=24h` sobrando abriria uma lista vazia (e a API recusa).
 */
export function buscaAoClicarNoCartaoDePrazos<T extends object>(
  prev: T,
  recorte: ResumoDePrazos,
  ativo: boolean,
) {
  const semOrdenacao = { sort: undefined, dir: undefined };
  // A janela do widget (`dias`) vive e morre com a faixa: sem `vence` ela só
  // mudaria o cartão "Próximos N dias", e ninguém a vê na tela.
  if (ativo) {
    return {
      ...prev,
      status: undefined,
      vence: undefined,
      dias: undefined,
      ...(recorte === "dueSoon" ? semOrdenacao : {}),
      page: undefined,
    };
  }
  return {
    ...prev,
    ...(recorte === "pending" && (prev as RecorteDaBuscaDePrazos).sort === "vencimento"
      ? semOrdenacao
      : {}),
    vence: undefined,
    // "Próximos N dias" mantém a janela que o próprio cartão está mostrando.
    ...(recorte === "dueSoon" ? {} : { dias: undefined }),
    ...listaDePrazos(recorte).search,
    page: undefined,
  };
}

export interface DestinoDePericias {
  to: "/pericias";
  search: {
    status?: ExpertiseStatusKey[];
    /** TUT-476 — `com` / `sem` motivo estruturado, ou o id de UM motivo. */
    motivo?: string;
    /** TUT-476 — o período de agendamento (dias `AAAA-MM-DD`, inclusivos). */
    periodoDe?: string;
    periodoAte?: string;
  };
}

/**
 * A lista de perícias de um estado — o funil da Visão geral, os cartões de
 * `/pericias` e as pendências do Agendamento apontam para cá. Sem estado,
 * abre a fila inteira (o cartão "Total em acompanhamento").
 */
export function listaDePericias(status?: ExpertiseStatusKey): DestinoDePericias {
  return status
    ? { to: "/pericias", search: { status: [status] } }
    : { to: "/pericias", search: {} };
}

// ---------------------------------------------------------------------------
// TUT-476 (regra D9) — TODO widget da Visão geral mostra o que conta: o número
// ou a faixa leva à lista com o recorte EXATO usado para contar, na URL. Os
// destinos abaixo são os que faltavam; o catálogo (`dashboardDestinos.ts`)
// declara qual widget usa qual, e a e2e `widgets-levam-ao-filtro.spec.ts`
// clica em cada um e confere a URL, o chip e o total.
// ---------------------------------------------------------------------------

export interface DestinoDeProcessos {
  to: "/processos";
  search: {
    subjectId?: string[];
    abertos?: true;
    periodoDe?: string;
    periodoAte?: string;
  };
}

/**
 * "Assuntos periciais": o assunto canônico conta os processos ABERTOS que o
 * têm (`dashboardExpertiseSubjects`), então a lista leva o assunto e `abertos`.
 * Sem `abertos` a faixa abriria os concluídos que ela deixou de fora.
 */
export function listaDeProcessosPorAssunto(subjectId: string): DestinoDeProcessos {
  return { to: "/processos", search: { subjectId: [subjectId], abertos: true } };
}

/**
 * O motivo de encerramento na URL de `/pericias`: `com`, `sem` ou o UUID de um
 * motivo — o MESMO que o DTO da API aceita (`MOTIVO_DA_PERICIA`), então lixo
 * não chega ao servidor para virar 400 e tela de erro. Os motivos do demo
 * também têm id em forma de UUID, pelo mesmo motivo. Lido pela rota e pela tela
 * (o `useSearch()` da filha chega mesclado com a busca do pai).
 */
export function motivoDaPericia(value: unknown): string | undefined {
  return typeof value === "string" &&
    /^(?:com|sem|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/iu.test(value)
    ? value
    : undefined;
}

/** O desfecho de "Frustrações e remarcações": a barra e o motivo dela. */
export type DesfechoDeExcecao = "rescheduled" | "frustrated";

/**
 * "Frustrações e remarcações": a barra do desfecho abre `/pericias` no status
 * dele; o motivo (`com` / `sem` motivo estruturado, ou o id de um motivo) é o
 * segundo recorte, e a linha de motivo conta só as ocorrências DAQUELE
 * desfecho — por isso o status vai junto.
 */
export function listaDeExcecoes(
  desfecho: DesfechoDeExcecao,
  motivo?: string,
): DestinoDePericias {
  return {
    to: "/pericias",
    search: { status: [desfecho], ...(motivo ? { motivo } : {}) },
  };
}

export interface DestinoDePrazosPorPeriodo {
  to: "/prazos";
  search: {
    status?: StatusDePrazo;
    periodoDe?: string;
    periodoAte?: string;
    concluidoDe?: string;
    concluidoAte?: string;
    noPrazo?: true;
  };
}

/**
 * A conclusão que a busca de `/prazos` pode mandar à API, saneada num lugar só
 * (a rota e a tela chamam esta função): o período em que o prazo foi concluído
 * e o `noPrazo` da Medição só valem com `status=completed` — com outro status a
 * API recusa a combinação com 400, e uma URL salva abriria a tela de erro (a
 * mesma lição do `vence`, `faixaDaBuscaDePrazos`).
 */
export function conclusaoDaBuscaDePrazos(busca: {
  status?: unknown;
  concluidoDe?: unknown;
  concluidoAte?: unknown;
  noPrazo?: unknown;
}): { concluidoDe?: string; concluidoAte?: string; noPrazo?: true } {
  if (busca.status !== "completed") return {};
  const { periodoDe, periodoAte } = periodoDaBusca({
    periodoDe: busca.concluidoDe,
    periodoAte: busca.concluidoAte,
  });
  const noPrazo =
    busca.noPrazo === true || busca.noPrazo === "1" || busca.noPrazo === "true";
  return {
    ...(periodoDe ? { concluidoDe: periodoDe } : {}),
    ...(periodoAte ? { concluidoAte: periodoAte } : {}),
    ...(noPrazo ? { noPrazo: true as const } : {}),
  };
}

/** Série temporal "Prazos vencendo": o balde é o vencimento no período. */
export function listaDePrazosDoPeriodo(
  periodoDe: string,
  periodoAte: string,
): DestinoDePrazosPorPeriodo {
  return { to: "/prazos", search: { periodoDe, periodoAte } };
}

/** Série temporal "Perícias agendadas": o balde é o agendamento no período. */
export function listaDePericiasDoPeriodo(
  periodoDe: string,
  periodoAte: string,
): DestinoDePericias {
  return { to: "/pericias", search: { periodoDe, periodoAte } };
}

/** Série temporal "Processos novos": o balde é a entrada no período. */
export function listaDeProcessosDoPeriodo(
  periodoDe: string,
  periodoAte: string,
): DestinoDeProcessos {
  return { to: "/processos", search: { periodoDe, periodoAte } };
}

// ---------------------------------------------------------------------------
// TUT-476 (integração com a TUT-480) — a Fila de laudos e a Medição de laudos
// abrem a aba Laudos da Elaboração, recortada por etapa, por quem elabora, pelo
// revisor e pela data de protocolo (o recorte que a TUT-480 construiu). As
// chaves são as de `ElaboracaoSearch`.
// ---------------------------------------------------------------------------

export interface DestinoDeLaudos {
  to: "/elaboracao";
  search: {
    atividade: "laudos";
    etapa: EtapaDeLaudo[];
    /** Quem ELABORA o laudo (`expert_reports.assigned_user_id`). */
    atribuido?: string;
    /** O revisor designado (`expert_reports.assigned_reviewer_user_id`). */
    revisor?: string;
    /** A data de PROTOCOLO (`filed_at`), dias `AAAA-MM-DD` inclusivos. */
    periodoDe?: string;
    periodoAte?: string;
  };
}

/** As etapas que a Fila de laudos conta como ATIVAS (as três antes do protocolo). */
export const ETAPAS_ATIVAS_DO_LAUDO = [
  "drafting",
  "in_review",
  "awaiting_filing",
] as const satisfies readonly EtapaDeLaudo[];

/**
 * "Seus, por etapa": os laudos que a pessoa ELABORA naquela etapa. É o recorte
 * mais fiel que um link escreve: o "meu" do widget é "elaboro OU, em revisão,
 * reviso" (`laudoEhMeu`), e a URL tem `atribuido` ou `revisor`, nunca o OU. A
 * metade que o link deixa de fora é a de quem só revisa, e só na etapa "em
 * revisão" — declarada no catálogo (`aproximado`).
 */
export function listaDeLaudosQueElaboro(
  etapa: EtapaDeLaudo,
  usuarioId: string,
): DestinoDeLaudos {
  return {
    to: "/elaboracao",
    search: { atividade: "laudos", etapa: [etapa], atribuido: usuarioId },
  };
}

/**
 * "Elaboração no escritório": os laudos ATIVOS (em elaboração, em revisão e
 * aguardando protocolo) que a pessoa elabora — a contagem do widget por
 * responsável (`assignees`), exata.
 */
export function listaDeLaudosEmElaboracaoDe(usuarioId: string): DestinoDeLaudos {
  return {
    to: "/elaboracao",
    search: {
      atividade: "laudos",
      etapa: [...ETAPAS_ATIVAS_DO_LAUDO],
      atribuido: usuarioId,
    },
  };
}

/**
 * "Revisão designada no escritório": os laudos EM REVISÃO cujo revisor é a
 * pessoa — a contagem do widget por revisor (`reviewers`), exata.
 */
export function listaDeLaudosEmRevisaoDe(usuarioId: string): DestinoDeLaudos {
  return {
    to: "/elaboracao",
    search: { atividade: "laudos", etapa: ["in_review"], revisor: usuarioId },
  };
}

/** Medição "Laudos entregues": os protocolados no período (`filed_at`). */
export function listaDeLaudosProtocolados(
  periodoDe: string,
  periodoAte: string,
): DestinoDeLaudos {
  return {
    to: "/elaboracao",
    search: { atividade: "laudos", etapa: ["filed"], periodoDe, periodoAte },
  };
}

// `DestinoDePecas` e `listaDePecasPendentes` saíram na TUT-244 — ver o
// registro da re-decisão no cabeçalho. O recorte que elas declaravam vive hoje
// em `FOCOS["pendente-assistente"]` e `FOCOS["pendente-envio"]`, com a mesma
// leitura de `AWAITING_DELIVERY` (sem filtro de tipo, de propósito).
